import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { SelectFieldComponent } from '../../../../components/form/select-field/select-field.component';
import { SelectOption } from '../../../../components/form/select-field/select-field.config';
import { TextAreaFieldComponent } from '../../../../components/form/textarea-field/textarea-field.component';
import { LoaderComponent } from '../../../../components/widgets/loader/loader.component';
import { LoaderConfig } from '../../../../components/widgets/loader/loader.config';

import { QualityManagementService } from '../../../../services/quality-management.service';
import { RouteService } from '../../../../services/route.service';
import { LoaderService } from '../../../../services/loader.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { AccessControlService } from '../../../../services/access-control.service';

import { Planning, PlanningPayload, PlanningUser, planningUserFullName } from '../../../../models/entities/planning';
import { ActivitiesPlanningPostponeComponent } from '../../components/activities-planning-postpone/activities-planning-postpone.component';

@Component({
  selector: 'app-activities-planning-edit-page',
  imports: [
    ReactiveFormsModule,
    InputFieldComponent,
    SelectFieldComponent,
    TextAreaFieldComponent,
    LoaderComponent,
    ActivitiesPlanningPostponeComponent
  ],
  templateUrl: './activities-planning-edit-page.component.html',
  styleUrl: './activities-planning-edit-page.component.scss',
})
export class ActivitiesPlanningEditPageComponent implements OnInit, OnDestroy {

  @ViewChild(ActivitiesPlanningPostponeComponent) postponePanel?: ActivitiesPlanningPostponeComponent;

  planningId?: number;
  planning?: Planning;
  isEditMode: boolean = false;

  form: FormGroup;
  assigneeOptions: SelectOption[] = [];

  currentUser: any = null;
  canModify: boolean = false;
  isLoading: boolean = false;
  loaderConfig: LoaderConfig = {
    type: 'spinner',
    text: 'Saving...',
    overlay: true
  };

  private readonly LOADER_CONTEXT = 'activities-planning-edit-page';
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private qualityManagementService: QualityManagementService,
    private routeService: RouteService,
    private loaderService: LoaderService,
    private alertNotificationService: AlertNotificationService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService
  ) {
    this.form = this.formBuilder.group({
      title: ['', [Validators.required]],
      description: ['', [Validators.required]],
      assigneeUserId: [null, [Validators.required]],
      dueDate: ['', [Validators.required]],
      reminder: ['']
    }, { validators: qualityManagementService.dueDateAfterReminderValidator('dueDate', 'reminder') });
  }

  ngOnInit(): void {
    this.mainLayoutService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user;
        this.applyReminderPermission();
      });

    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.canModify = this.accessControlService.hasFeatureActionAccess(
          profileCode,
          'QUALITY_MANAGEMENT_ACTIVITIES_PLANNING',
          'modify'
        );
        this.applyModifyPermission();
      });

    this.loaderService.getLoadingAsObservable(this.LOADER_CONTEXT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => this.isLoading = isLoading);

    this.loadAssignableUsers();

    const paramId = this.route.snapshot.paramMap.get('planningId');
    if (paramId) {
      this.planningId = Number(paramId);
      this.isEditMode = true;
      this.loadPlanning(this.planningId);
    }
  }

  ngOnDestroy(): void {
    this.loaderService.stopLoading(this.LOADER_CONTEXT);
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isCompleted(): boolean {
    return this.qualityManagementService.isPlanningCompleted(this.planning);
  }

  get isCreator(): boolean {
    if (!this.isEditMode) {
      return true;
    }
    return this.currentUser?.userId != null
      && Number(this.planning?.creatorUser?.userId) === Number(this.currentUser.userId);
  }

  get reportedByLabel(): string {
    if (this.planning?.creatorUser) {
      return planningUserFullName(this.planning.creatorUser);
    }
    return this.currentUser
      ? `${this.currentUser.lastName ?? ''} ${this.currentUser.firstName ?? ''}`.trim()
      : '';
  }

  get code(): string {
    return this.planning?.code ?? '';
  }

  getControl(name: string): FormControl {
    return this.form.get(name) as FormControl;
  }

  hasError(name: string): boolean {
    const control = this.form.get(name);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  errorMessage(name: string): string {
    const control = this.form.get(name);

    if (!control?.errors) {
      return '';
    }
    if (control.errors['backendError']) {
      return control.errors['backendError'];
    }
    if (control.errors['required']) {
      return 'Required field';
    }
    return '';
  }


  private loadAssignableUsers(): void {
    const params = new HttpParams().set('size', '9999');

    this.qualityManagementService.listPlanningAssignableUsers({ params }).subscribe({
      next: (response: any) => {
        const users: PlanningUser[] = this.qualityManagementService.readResponseKey(response, 'users', 'assignableUsers') || [];
        this.assigneeOptions = [
          { key: '', value: 'Select an assignee' },
          ...users.map(user => ({ key: user.userId, value: planningUserFullName(user) }))
        ];
      },
      error: (error: any) => {
        console.error('Error loading assignable users:', error);
        this.alertNotificationService.error('Failed to load the assignable users list.');
      }
    });
  }

  private loadPlanning(planningId: number): void {
    this.loaderService.startLoading(this.LOADER_CONTEXT);

    this.qualityManagementService.detailPlanning(planningId).subscribe({
      next: (response: any) => {
        const planning: Planning | null = this.qualityManagementService.readResponseKey(response, 'activityPlanning') ?? null;
        this.loaderService.stopLoading(this.LOADER_CONTEXT);

        if (!planning) {
          this.alertNotificationService.error('Planning not found.');
          return;
        }

        this.applyPlanning(planning);
      },
      error: (error: any) => {
        console.error('Error loading planning:', error);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.alertNotificationService.error('Failed to load the planning.');
      }
    });
  }

  private applyPlanning(planning: Planning): void {
    this.planning = planning;
    this.planningId = planning.activityPlanningId ?? this.planningId;

    this.form.patchValue({
      title: planning.title ?? '',
      description: planning.description ?? '',
      assigneeUserId: planning.assigneeUser?.userId ?? '',
      dueDate: this.qualityManagementService.toDateTimeLocal(planning.dueDate),
      reminder: this.qualityManagementService.toDateTimeLocal(planning.reminder)
    }, { emitEvent: false });

    this.applyReminderPermission();
  }

  private applyModifyPermission(): void {
    if (this.canModify) {
      this.form.enable({ emitEvent: false });
      this.applyReminderPermission();
      return;
    }

    this.form.disable({ emitEvent: false });
  }

  private applyReminderPermission(): void {
    if (!this.canModify) {
      this.form.disable({ emitEvent: false });
      return;
    }

    const reminderControl = this.form.get('reminder');
    if (!reminderControl) {
      return;
    }

    if (this.isCreator && !this.isCompleted) {
      reminderControl.enable({ emitEvent: false });
    } else {
      reminderControl.disable({ emitEvent: false });
    }
  }


  submitForm(): void {
    if (!this.canModify) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertNotificationService.error('Please fill in all the required fields.');
      return;
    }

    this.savePlanning(this.buildPayload(), 'Planning saved and notification sent.');
  }

  onReminderChange(): void {
    if (!this.isEditMode || !this.planningId || !this.canModify) {
      return;
    }

    if (this.form.errors?.['dueDateBeforeReminder']) {
      this.form.get('dueDate')?.markAsTouched();
      this.alertNotificationService.error("Due Date can't be earlier than the Reminder.");
      return;
    }

    this.handleSave(
      this.qualityManagementService.updatePlanningReminder(
        this.planningId,
        this.qualityManagementService.fromDateTimeLocal(this.form.get('reminder')?.value)
      ),
      'Reminder updated.'
    );
  }

  markAsDone(): void {
    if (!this.planningId || !this.canModify) {
      return;
    }

    this.handleSave(
      this.qualityManagementService.changePlanningStatus(this.planningId, 'COMPLETED'),
      'Planning marked as done.'
    );
  }

  onPostpone(reason: string): void {
    if (!this.planningId || !this.canModify) {
      return;
    }

    this.handleSave(
      this.qualityManagementService.postponePlanning(this.planningId, this.buildPayload(), reason),
      'Planning postponed.'
    );

    this.postponePanel?.closePanel();
  }

  private buildPayload(): PlanningPayload {
    const value = this.form.getRawValue();

    return {
      title: value.title,
      description: value.description,
      assigneeUserId: Number(value.assigneeUserId),
      dueDate: this.qualityManagementService.fromDateTimeLocal(value.dueDate) ?? ''
    };
  }

  private savePlanning(payload: PlanningPayload, successMessage: string): void {
    const reminder = this.qualityManagementService.fromDateTimeLocal(this.form.getRawValue().reminder);

    const request$ = this.planningId
      ? this.qualityManagementService.updatePlanning(this.planningId, payload)
      : this.qualityManagementService.createPlanning(payload, reminder);

    this.handleSave(request$, successMessage);
  }

  private handleSave(request$: Observable<any>, successMessage: string): void {
    this.loaderService.startLoading(this.LOADER_CONTEXT);

    request$.subscribe({
      next: (response: any) => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);

        const planning: Planning | null = this.qualityManagementService.readResponseKey(response, 'activityPlanning') ?? null;

        if (!planning) {
          if (this.planningId) {
            this.loadPlanning(this.planningId);
            this.alertNotificationService.success(successMessage);
          } else {
            this.applyBackendErrors(response);
          }
          return;
        }

        const isFirstSave = !this.isEditMode;
        this.applyPlanning(planning);
        this.alertNotificationService.success(successMessage);

        if (isFirstSave && planning.activityPlanningId) {
          this.routeService.activitiesPlanningDetail(Number(planning.activityPlanningId));
        }
      },
      error: (error: any) => {
        console.error('Error saving planning:', error);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.applyBackendErrors(error?.error);
      }
    });
  }

  private applyBackendErrors(response: any): void {
    const errors = response?.errors ?? response?.data?.errors ?? null;

    if (!errors || typeof errors !== 'object') {
      this.alertNotificationService.error('Failed to save the planning.');
      return;
    }

    Object.keys(errors).forEach(field => {
      const control = this.form.get(field);
      if (control) {
        control.markAsTouched();
        control.setErrors({ ...(control.errors ?? {}), backendError: errors[field] });
      }
    });

    this.alertNotificationService.error(Object.values(errors).join(' '));
  }
}
